import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ErrorLogsPanel } from "@/components/ErrorLogsPanel";
import { Star, Check, EyeOff, Sparkles, Clock, Send, Trash2, Receipt, Download } from "lucide-react";

const STATUS_META = {
  pending: { label: "En attente", color: "bg-[#D4A373]/20 text-[#B5843F]", icon: Clock },
  approved: { label: "Publié", color: "bg-[#7A8B76]/20 text-[#5f7059]", icon: Check },
  hidden: { label: "Masqué", color: "bg-[#E6E2DD] text-[#8A8480]", icon: EyeOff },
};

export default function AdminTestimonials() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [sendingReminders, setSendingReminders] = useState(false);
  const [stats, setStats] = useState(null);
  const [accountType, setAccountType] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [sales, setSales] = useState(null);
  const [showSales, setShowSales] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.account_type !== "admin") {
      nav("/");
      return;
    }
    load();
  }, [user, authLoading]);

  const load = () => {
    api
      .get("/admin/testimonials")
      .then(({ data }) => setItems(data))
      .catch(() => toast.error("Chargement impossible"))
      .finally(() => setLoading(false));
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
  };

  const moderate = async (t, status, featured) => {
    try {
      await api.post(`/admin/testimonials/${t.id}/moderate`, { status, featured });
      setItems((prev) =>
        prev.map((x) => (x.id === t.id ? { ...x, status, featured } : x))
      );
      toast.success("Mis à jour.");
    } catch (e) {
      toast.error("Erreur");
    }
  };

  const sendReminders = async () => {
    setSendingReminders(true);
    try {
      const { data } = await api.post("/admin/send-reminders");
      toast.success(
        data.sent > 0
          ? `${data.sent} relance(s) envoyée(s) aux utilisateurs inactifs.`
          : "Aucun utilisateur à relancer pour le moment."
      );
    } catch (e) {
      toast.error("Envoi impossible.");
    } finally {
      setSendingReminders(false);
    }
  };

  const openAccounts = (type, label) => {
    if (accountType === type) {
      setAccountType(null);
      setAccounts([]);
      return;
    }
    setAccountType(type);
    setAccounts([]);
    setLoadingAccounts(true);
    api
      .get("/admin/accounts", { params: { type } })
      .then(({ data }) => setAccounts(data))
      .catch(() => toast.error("Chargement impossible"))
      .finally(() => setLoadingAccounts(false));
  };

  const deleteAccount = async (u) => {
    if (!window.confirm(`Supprimer définitivement le compte de ${u.name} (${u.email}) et toutes ses données ?`)) return;
    try {
      await api.delete(`/admin/accounts/${u.id}`);
      setAccounts((prev) => prev.filter((x) => x.id !== u.id));
      toast.success("Compte supprimé.");
      api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Suppression impossible.");
    }
  };

  const exportCsv = () => {
    if (!accounts.length) return;
    const header = ["Nom", "Email", "Type", "Date d'inscription"];
    const typeLabel = { couple: "Couple", solo: "Célibataire", pastor: "Pasteur", admin: "Admin" };
    const rows = accounts.map((u) => [
      u.name || "",
      u.email || "",
      typeLabel[u.account_type] || u.account_type || "",
      u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : "",
    ]);
    const escape = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [header, ...rows].map((r) => r.map(escape).join(",")).join("\r\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `emails-${accountType || "tous"}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${accounts.length} email(s) exporté(s).`);
  };

  const toggleSales = () => {
    if (showSales) {
      setShowSales(false);
      return;
    }
    setShowSales(true);
    if (sales === null) {
      api.get("/admin/sales").then(({ data }) => setSales(data)).catch(() => toast.error("Chargement des ventes impossible"));
    }
  };

  const filtered = items.filter((t) => filter === "all" || t.status === filter);  const counts = {
    all: items.length,
    pending: items.filter((t) => t.status === "pending").length,
    approved: items.filter((t) => t.status === "approved").length,
    hidden: items.filter((t) => t.status === "hidden").length,
  };

  return (
    <div className="max-w-5xl mx-auto px-6 md:px-10 py-12 md:py-16">
      <p className="text-xs tracking-[0.3em] uppercase text-[#C86B5E] font-semibold mb-3">
        Administration
      </p>
      <div className="flex items-start justify-between gap-4 flex-wrap mb-8">
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight leading-tight">
          Modération des <span className="italic">témoignages</span>
        </h1>
        <button
          data-testid="send-reminders-button"
          onClick={sendReminders}
          disabled={sendingReminders}
          className="inline-flex items-center gap-2 text-sm border border-[#E6E2DD] hover:border-[#C86B5E] text-[#5C5754] hover:text-[#C86B5E] rounded-full px-5 py-2.5 transition-colors disabled:opacity-50"
          title="Relancer par email les utilisateurs inscrits mais inactifs"
        >
          <Send className="w-4 h-4" />
          {sendingReminders ? "Envoi…" : "Envoyer les relances"}
        </button>
      </div>

      {stats && (
        <div className="mb-10">
          <p className="text-xs tracking-[0.2em] uppercase text-[#8A8480] mb-3">
            Comptes · cliquez une catégorie pour voir les emails
          </p>
          <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {[
              { label: "Comptes créés", value: stats.total_accounts, type: "all", testid: "stat-total-accounts" },
              { label: "Comptes couple", value: stats.couple_accounts, type: "couple", testid: "stat-couple-accounts" },
              { label: "Célibataires", value: stats.solo_accounts, type: "solo", testid: "stat-solo" },
              { label: "Pasteurs", value: stats.pastor_accounts, type: "pastor", testid: "stat-pastors" },
            ].map((s) => (
              <button
                key={s.testid}
                data-testid={s.testid}
                onClick={() => openAccounts(s.type, s.label)}
                className={`text-left bg-white border rounded-2xl p-5 transition-all hover:border-[#C86B5E] ${
                  accountType === s.type ? "border-[#C86B5E] ring-1 ring-[#C86B5E]/30" : "border-[#E6E2DD]"
                }`}
              >
                <p className="text-xs tracking-[0.15em] uppercase text-[#8A8480] mb-1">{s.label}</p>
                <p className="font-heading text-3xl text-[#2C2A29]">{s.value}</p>
                <p className="text-[10px] text-[#C86B5E] mt-1">Voir les emails →</p>
              </button>
            ))}
          </div>

          {accountType && (
            <div data-testid="accounts-list" className="bg-white border border-[#E6E2DD] rounded-2xl p-5 mb-4 max-h-80 overflow-y-auto">
              {!loadingAccounts && accounts.length > 0 && (
                <div className="flex justify-end mb-3">
                  <button
                    data-testid="export-emails-csv-button"
                    onClick={exportCsv}
                    className="inline-flex items-center gap-2 text-xs border border-[#E6E2DD] hover:border-[#C86B5E] text-[#5C5754] hover:text-[#C86B5E] rounded-full px-4 py-2 transition-colors"
                    title="Télécharger les emails de cette catégorie au format CSV"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Exporter en CSV ({accounts.length})
                  </button>
                </div>
              )}
              {loadingAccounts ? (
                <p className="text-sm text-[#8A8480]">Chargement…</p>
              ) : accounts.length === 0 ? (
                <p className="text-sm text-[#8A8480]">Aucun compte dans cette catégorie.</p>
              ) : (
                <ul className="divide-y divide-[#F3EFEB]">
                  {accounts.map((u, i) => (
                    <li key={i} className="py-2.5 flex items-center justify-between gap-4">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-[#2C2A29] truncate">{u.name}</p>
                        <a href={`mailto:${u.email}`} className="text-sm text-[#C86B5E] hover:underline break-all">{u.email}</a>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0">
                        <span className="text-xs text-[#8A8480]">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString("fr-FR") : ""}
                        </span>
                        <button
                          data-testid={`delete-account-${u.id}`}
                          onClick={() => deleteAccount(u)}
                          className="text-[#B04A3F] hover:bg-[#B04A3F]/10 rounded-full p-2 transition-colors"
                          title="Supprimer ce compte"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <p className="text-xs tracking-[0.2em] uppercase text-[#8A8480] mb-3">Activité & ventes</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Couples liés", value: stats.linked_couples, testid: "stat-linked-couples" },
              { label: "Bilans générés", value: stats.analyses, testid: "stat-analyses" },
              { label: "Taux de complétion", value: `${stats.completion_rate}%`, testid: "stat-completion" },
              { label: "Revenu", value: `${stats.revenue}€`, testid: "stat-revenue" },
              { label: "Modules vendus", value: stats.modules_sold, testid: "stat-modules-sold" },
              { label: "Packs vendus", value: stats.packs_sold, testid: "stat-packs-sold" },
              { label: "Témoignages", value: stats.testimonials_total, testid: "stat-testimonials" },
              { label: "À modérer", value: stats.testimonials_pending, testid: "stat-pending" },
            ].map((s) => (
              <div key={s.testid} data-testid={s.testid} className="bg-white border border-[#E6E2DD] rounded-2xl p-5">
                <p className="text-xs tracking-[0.15em] uppercase text-[#8A8480] mb-1">{s.label}</p>
                <p className="font-heading text-3xl text-[#2C2A29]">{s.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-4">
            <button
              data-testid="toggle-sales-button"
              onClick={toggleSales}
              className="inline-flex items-center gap-2 text-sm border border-[#E6E2DD] hover:border-[#C86B5E] text-[#5C5754] hover:text-[#C86B5E] rounded-full px-5 py-2.5 transition-colors"
            >
              <Receipt className="w-4 h-4" />
              {showSales ? "Masquer les ventes" : "Voir les ventes détaillées"}
            </button>
            {showSales && (
              <div data-testid="sales-list" className="mt-4 bg-white border border-[#E6E2DD] rounded-2xl p-5 max-h-96 overflow-y-auto">
                {sales === null ? (
                  <p className="text-sm text-[#8A8480]">Chargement…</p>
                ) : sales.length === 0 ? (
                  <p className="text-sm text-[#8A8480]">Aucune vente pour l'instant.</p>
                ) : (
                  <ul className="divide-y divide-[#F3EFEB]">
                    {sales.map((s) => (
                      <li key={s.id} className="py-2.5 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#2C2A29]">
                            {s.label}
                            <span className="ml-2 text-xs text-[#8A8480]">
                              {s.item_type === "pack" ? "Pack" : "Module"}
                            </span>
                          </p>
                          <p className="text-xs text-[#8A8480] break-all">{s.buyer_name} · {s.buyer_email}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-heading text-lg text-[#7A8B76]">{s.amount}€</p>
                          <p className="text-xs text-[#8A8480]">{s.date ? new Date(s.date).toLocaleDateString("fr-FR") : ""}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
          <ErrorLogsPanel />
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-8">
        {["all", "pending", "approved", "hidden"].map((f) => (
          <button
            key={f}
            data-testid={`admin-filter-${f}`}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-full text-sm border transition-all ${
              filter === f
                ? "border-[#C86B5E] bg-[#C86B5E]/5 text-[#2C2A29]"
                : "border-[#E6E2DD] bg-white hover:border-[#C86B5E]/40 text-[#5C5754]"
            }`}
          >
            {f === "all" ? "Tous" : STATUS_META[f].label} ({counts[f]})
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-[#8A8480]">Chargement…</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#E6E2DD] rounded-3xl">
          <p className="font-heading text-2xl mb-2">Aucun témoignage</p>
          <p className="text-[#5C5754]">Les témoignages soumis apparaîtront ici.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((t) => {
            const meta = STATUS_META[t.status] || STATUS_META.pending;
            const Icon = meta.icon;
            return (
              <div
                key={t.id}
                data-testid={`admin-testimonial-${t.id}`}
                className="bg-white border border-[#E6E2DD] rounded-2xl p-6"
              >
                <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                  <div>
                    <p className="font-heading text-lg text-[#2C2A29]">
                      {t.display_name}{" "}
                      <span className="text-sm text-[#8A8480] font-normal">· {t.status_label}</span>
                    </p>
                    <div className="flex items-center gap-0.5 mt-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className="w-4 h-4"
                          strokeWidth={1.5}
                          style={{
                            fill: t.rating >= n ? "#D4A373" : "transparent",
                            color: t.rating >= n ? "#D4A373" : "#C9C2BA",
                          }}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {t.featured && (
                      <span className="inline-flex items-center gap-1 text-xs text-[#B5843F] bg-[#D4A373]/15 rounded-full px-2.5 py-1">
                        <Sparkles className="w-3 h-3" /> Mis en avant
                      </span>
                    )}
                    <span className={`inline-flex items-center gap-1 text-xs rounded-full px-2.5 py-1 ${meta.color}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </div>
                </div>
                <p className="text-[#2C2A29] leading-relaxed whitespace-pre-wrap mb-4">« {t.content} »</p>
                <div className="flex flex-wrap gap-2 pt-3 border-t border-[#F3EFEB]">
                  <button
                    data-testid={`approve-${t.id}`}
                    onClick={() => moderate(t, "approved", t.featured)}
                    className="text-sm bg-[#7A8B76] hover:bg-[#6a7a66] text-white rounded-full px-4 py-2 transition-colors"
                  >
                    Publier
                  </button>
                  <button
                    data-testid={`feature-${t.id}`}
                    onClick={() => moderate(t, "approved", !t.featured)}
                    className="text-sm border border-[#D4A373] text-[#B5843F] hover:bg-[#D4A373]/10 rounded-full px-4 py-2 transition-colors"
                  >
                    {t.featured ? "Retirer la mise en avant" : "Mettre en avant"}
                  </button>
                  <button
                    data-testid={`hide-${t.id}`}
                    onClick={() => moderate(t, "hidden", false)}
                    className="text-sm border border-[#E6E2DD] text-[#8A8480] hover:border-[#C86B5E] hover:text-[#C86B5E] rounded-full px-4 py-2 transition-colors"
                  >
                    Masquer
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
